import { ExternalLink } from 'lucide-react';

import { buildGitHubUrl, extractGitHubMeta, formatSourceLabel } from '../lib/github-url';
import { oneLight, SyntaxHighlighter } from '../lib/syntax';
import { CopyButton } from './CopyButton';
import { StreamingText } from './StreamingText';

interface CodeBlockProps {
  content: string;
  metadata?: unknown;
}

function getLanguage(metadata: unknown): string | null {
  if (
    metadata &&
    typeof metadata === 'object' &&
    'language' in metadata &&
    typeof (metadata as { language: unknown }).language === 'string'
  ) {
    return (metadata as { language: string }).language;
  }
  return null;
}

export function CodeBlock({ content, metadata }: CodeBlockProps): React.ReactElement {
  const language = getLanguage(metadata);
  const github = extractGitHubMeta(metadata);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-muted">
      {github && (
        <a
          href={buildGitHubUrl(github)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 border-b border-border px-3 py-1.5 font-mono text-[0.6875rem] text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
        >
          <span className="truncate">{formatSourceLabel(github)}</span>
          <ExternalLink size={11} className="shrink-0" />
        </a>
      )}
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        {language ? (
          <span className="text-[0.625rem] font-medium tracking-wider text-muted-foreground uppercase">{language}</span>
        ) : (
          <span />
        )}
        <CopyButton text={content} />
      </div>
      {language === 'markdown' ? (
        <div className="p-3 text-sm">
          <StreamingText content={content} />
        </div>
      ) : language ? (
        <SyntaxHighlighter
          language={language}
          style={oneLight}
          customStyle={{
            margin: 0,
            padding: '0.75rem',
            background: 'transparent',
            fontSize: '0.8125rem',
          }}
        >
          {content}
        </SyntaxHighlighter>
      ) : (
        <pre className="overflow-x-auto p-3">
          <code className="text-[0.8125rem] text-foreground">{content}</code>
        </pre>
      )}
    </div>
  );
}
